import React, { useEffect, useState } from 'react';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { DeacParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { ActivityIndicator, ScrollView, TouchableOpacity, View } from 'react-native';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import DoughnutChart from './DoughnutChart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faStar } from '@fortawesome/free-solid-svg-icons';
import PeriodDropdown, { PeriodType } from '../../../components/deac/PeriodDropdown';
import moment from 'moment';
import RecentVacanciesDashboard from './RecentVacanciesDashboard';
import Toast from 'react-native-toast-message';
import { deacService } from '../../../services/central-api/deac';
import { WindowInformation } from '../../../services/window-information';
import DashboardReport from './DashboardReport';
import MyDateRangePicker, { Value, ValuePiece } from '../../../components/MyDateRangePicker';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import { RootNavigation } from '../../../Routes';

export default function Dashboard({ navigation }: { navigation: RootNavigation; }) {
    const route = useRoute<RouteProp<DeacParamList, 'Dashboard'>>();
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<DeacParamList['Dashboard']>({
        ...route.params,
        beginIn: moment().startOf('day').subtract(30, 'days').valueOf(),
        endIn: moment().valueOf()
    });

    const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('monthly');
    const [dateFrom, setDateFrom] = useState<ValuePiece>(null);
    const [dateTo, setDateTo] = useState<ValuePiece>(null);

    function getAgentPositionColor(index: number) {
        if (index === 1) {
            return '#CC8924';
        } else if (index === 2) {
            return '#7A7A7A';
        } else if (index === 3) {
            return '#9C6652';
        }

        return 'transparent';
    }

    function getPeriodMessage() {
        if (dateFrom && dateTo) {
            return `${moment(dateFrom).format('DD/MM')} - ${moment(dateTo).format('DD/MM')}`;
        } else if (selectedPeriod === 'today') {
            return translate('today');
        } else if (selectedPeriod === 'weekly') {
            return translate('last7days');
        } else if (selectedPeriod === 'fortnightly') {
            return translate('last15days');
        } else if (selectedPeriod === 'monthly') {
            return translate('last30days');
        } else if (selectedPeriod === 'semiannual') {
            return translate('last6Months');
        }

        return translate('allPeriod');
    }

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['deacDashboard', filters],
        queryFn: () => deacService.getDashboardData(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, error, isError } = queryResult;

    useEffect(() => {
        if (isError) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }, [error, isError]);

    useEffect(() => {
        if (selectedPeriod != 'select') {
            setDateFrom(null);
            setDateTo(null);
            calculatePeriod();
        }
    }, [selectedPeriod]);

    function calculatePeriod() {
        let beginIn = undefined;
        const endIn = moment().valueOf();

        if (selectedPeriod === 'today') {
            beginIn = moment().startOf('day').valueOf();
        } else if (selectedPeriod === 'weekly') {
            beginIn = moment().startOf('day').subtract(7, 'days').valueOf();
        } else if (selectedPeriod === 'fortnightly') {
            beginIn = moment().startOf('day').subtract(15, 'days').valueOf();
        } else if (selectedPeriod === 'monthly') {
            beginIn = moment().startOf('day').subtract(30, 'days').valueOf();
        } else if (selectedPeriod === 'semiannual') {
            beginIn = moment().startOf('day').subtract(6, 'months').valueOf();
        }

        setBeginInAndEndIn(beginIn, endIn);
    }

    function setBeginInAndEndIn(beginIn: number | undefined, endIn: number) {
        navigation.setParams({
            ...filters,
            beginIn: beginIn,
            endIn: endIn
        });

        setFilters({
            ...filters,
            beginIn: beginIn,
            endIn: endIn
        });
    }

    function getVacanciesGeneralSummaryLabels() {
        const labels = [];

        if (data?.vacanciesGeneralSummary.scheduled) {
            labels.push(translate('scheduled'));
        }
        if (data?.vacanciesGeneralSummary.opened) {
            labels.push(translate('opened'));
        }
        if (data?.vacanciesGeneralSummary.waiting) {
            labels.push(translate('waiting'));
        }
        if (data?.vacanciesGeneralSummary.progress) {
            labels.push(translate('inProgress'));
        }
        if (data?.vacanciesGeneralSummary.finished) {
            labels.push(translate('finished'));
        }
        if (data?.vacanciesGeneralSummary.canceled) {
            labels.push(translate('canceled'));
        }

        return labels;
    }

    function getVacanciesGeneralSummary() {
        const result = [];

        if (data?.vacanciesGeneralSummary.scheduled) {
            result.push(data.vacanciesGeneralSummary.scheduled);
        }
        if (data?.vacanciesGeneralSummary.opened) {
            result.push(data.vacanciesGeneralSummary.opened);
        }
        if (data?.vacanciesGeneralSummary.waiting) {
            result.push(data.vacanciesGeneralSummary.waiting);
        }
        if (data?.vacanciesGeneralSummary.progress) {
            result.push(data.vacanciesGeneralSummary.progress);
        }
        if (data?.vacanciesGeneralSummary.finished) {
            result.push(data.vacanciesGeneralSummary.finished);
        }
        if (data?.vacanciesGeneralSummary.canceled) {
            result.push(data.vacanciesGeneralSummary.canceled);
        }

        return result;
    }

    function getVacanciesGeneralSummaryColors() {
        const result = [];
        if (data?.vacanciesGeneralSummary.scheduled) {
            result.push('#438CC0');
        }
        if (data?.vacanciesGeneralSummary.opened) {
            result.push('#E9A76B');
        }
        if (data?.vacanciesGeneralSummary.waiting) {
            result.push('#E2E0AB');
        }
        if (data?.vacanciesGeneralSummary.progress) {
            result.push('#6B71E9');
        }
        if (data?.vacanciesGeneralSummary.finished) {
            result.push('#4C9159');
        }
        if (data?.vacanciesGeneralSummary.canceled) {
            result.push('#BD5853');
        }

        return result;
    }

    function getVacanciesGeneralSummaryLinesColors() {
        const result = [];
        if (data?.vacanciesGeneralSummary.scheduled) {
            result.push('#216291');
        }
        if (data?.vacanciesGeneralSummary.opened) {
            result.push('#9D5F26');
        }
        if (data?.vacanciesGeneralSummary.waiting) {
            result.push('#504D12');
        }
        if (data?.vacanciesGeneralSummary.progress) {
            result.push('#2E338E');
        }
        if (data?.vacanciesGeneralSummary.finished) {
            result.push('#1F5C2A');
        }
        if (data?.vacanciesGeneralSummary.canceled) {
            result.push('#822E2A');
        }

        return result;
    }

    function ensureMinimumLength(array: AgentRank[]) {
        const defaultValue: AgentRank = {
            name: '',
            dropoutCount: 0,
            id: -1,
            totalWorkHours: 0,
            workplaceCount: 0
        };

        if (array.length < 5) {
            return [...array, ...Array(5 - array.length).fill(defaultValue)];
        }
        return array;
    }

    return (
        <Container>
            <Header
                title={translate('dashboard')}
                zIndex={2}
                actions={<View style={{ flexDirection: 'row', gap: 8 }}>
                    <MyDateRangePicker
                        dateRangePickerStyle={{
                            borderWidth: 1,
                            borderColor: theme.colors.neutral['300']
                        }}
                        useCustomDateInfo={true}
                        dateFormat={'DD/MM'}
                        maxDate={moment().toDate()}
                        calendarPosition='left'
                        dateFrom={dateFrom}
                        setDateFrom={setDateFrom}
                        dateTo={dateTo}
                        setDateTo={setDateTo}
                        onChange={(value: Value) => {
                            if (Array.isArray(value)) {
                                setSelectedPeriod('select');

                                if (value[0] && value[1]) {
                                    const beginIn = moment(value[0]).startOf('day').valueOf();
                                    const endIn = moment(value[1]).endOf('day').valueOf();
                                    setBeginInAndEndIn(beginIn, endIn);
                                }
                            }
                        }}
                        onRemove={() => {
                            setDateFrom(null);
                            setDateTo(null);
                            setSelectedPeriod('allPeriod');
                        }}
                    />
                    <PeriodDropdown
                        setValue={setSelectedPeriod}
                        value={selectedPeriod}
                        zIndex={2}
                    />
                    {data && <DashboardReport
                        beginIn={filters.beginIn}
                        endIn={filters.endIn}
                        dashboardData={data}
                        isCustomPeriod={selectedPeriod === 'select'}
                        periodMessage={getPeriodMessage()}
                    />}
                </View>}
            />
            <ScrollView contentContainerStyle={styles.cardListWrapper}>
                <View style={styles.contentContainer}>
                    <View style={styles.chartsAndAgentsTableContainer(windowInfo.width)}>
                        <View style={styles.multipleChartsContainer(windowInfo.width)}>
                            <View style={styles.chartContainer(windowInfo.width <= 1024 ? 1 : 0.4)}>
                                <DoughnutChart
                                    labels={getVacanciesGeneralSummaryLabels()}
                                    chartData={getVacanciesGeneralSummary()}
                                    sectionColors={getVacanciesGeneralSummaryColors()}
                                    linesColors={getVacanciesGeneralSummaryLinesColors()}
                                    legendAlign='right'
                                    isLoading={isLoading}
                                >
                                    <DoughnutChart.Header>
                                        <View style={styles.chartHeaderContainer}>
                                            <MyAppText style={styles.chartHeaderTitle}>{translate('vacanciesGeneralSummary')}</MyAppText>
                                            <View style={styles.chartHeaderActions}>
                                                <MyAppText style={styles.chartHeaderPeriodText}>{getPeriodMessage()}</MyAppText>
                                                <TouchableOpacity onPress={() => navigation.navigate('Deac', { screen: 'Vacancies' })}>
                                                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} fontSize={16} color={theme.colors.primary['500']} />
                                                </TouchableOpacity>
                                            </View>
                                        </View>
                                    </DoughnutChart.Header>
                                </DoughnutChart>
                            </View>
                            <View style={styles.chartContainer(windowInfo.width <= 1024 ? 1 : 0.6)}>
                                <DoughnutChart
                                    labels={data?.mostCommonApplicationCancellationReason.map(item => item.name) ?? []}
                                    chartData={data?.mostCommonApplicationCancellationReason.map(item => item.quantity) ?? []}
                                    legendAlign='right'
                                    sectionColors={['#43C09D', '#5ECADD', '#6B71E9', '#DD5EB7', '#BD5853']}
                                    linesColors={['#216753', '#1D6B78', '#2E338E', '#87296B', '#842823']}
                                    isLoading={isLoading}
                                >
                                    <DoughnutChart.Header>
                                        <View style={styles.chartHeaderContainer}>
                                            <MyAppText style={styles.chartHeaderTitle}>{translate('mostCommonApplicationsCancellationReasons')}</MyAppText>
                                            <MyAppText style={styles.chartHeaderPeriodText}>{getPeriodMessage()}</MyAppText>
                                        </View>
                                    </DoughnutChart.Header>
                                </DoughnutChart>
                            </View>
                        </View>
                        <View style={styles.multipleChartsContainer(windowInfo.width)}>
                            <View style={styles.chartContainer(windowInfo.width <= 1024 ? 1 : 0.5)}>
                                <DoughnutChart
                                    labels={data?.workplaceWithMostVacancies.map(item => item.name) ?? []}
                                    chartData={data?.workplaceWithMostVacancies.map(item => item.quantity) ?? []}
                                    sectionColors={['#BD5853', '#A161E5', '#DDCE5E', '#E9A76B', '#438CC0']}
                                    linesColors={['#842823', '#58258F', '#6E6521', '#944F0E', '#145380']}
                                    isLoading={isLoading}
                                >
                                    <DoughnutChart.Header>
                                        <View style={styles.chartHeaderContainer}>
                                            <MyAppText style={styles.chartHeaderTitle}>{translate('vacanciesPerWorkplace')}</MyAppText>
                                            <MyAppText style={styles.chartHeaderPeriodText}>{getPeriodMessage()}</MyAppText>
                                        </View>
                                    </DoughnutChart.Header>
                                </DoughnutChart>
                            </View>
                            <View style={styles.chartContainer(windowInfo.width <= 1024 ? 1 : 0.5)}>
                                <DoughnutChart
                                    labels={data?.workplacesWithMostDropout.map(item => item.name) ?? []}
                                    chartData={data?.workplacesWithMostDropout.map(item => item.quantity) ?? []}
                                    sectionColors={['#BD5853', '#A161E5', '#DDCE5E', '#E9A76B', '#438CC0']}
                                    linesColors={['#842823', '#58258F', '#6E6521', '#944F0E', '#145380']}
                                    isLoading={isLoading}
                                >
                                    <DoughnutChart.Header>
                                        <View style={styles.chartHeaderContainer}>
                                            <MyAppText style={styles.chartHeaderTitle}>{translate('workplacesWithMoreWithdrawals')}</MyAppText>
                                            <MyAppText style={styles.chartHeaderPeriodText}>{getPeriodMessage()}</MyAppText>
                                        </View>
                                    </DoughnutChart.Header>
                                </DoughnutChart>
                            </View>
                        </View>
                        <View style={styles.agentsTable}>
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <MyAppText style={styles.chartHeaderTitle}>{translate('agentsRanking')}</MyAppText>
                                <MyAppText style={styles.chartHeaderPeriodText}>{getPeriodMessage()}</MyAppText>
                            </View>
                            <View style={styles.agentsTableTableHeader}>
                                <MyAppText style={styles.agentsTableTableHeaderText(windowInfo.width <= 1024 ? 0.4 : 0.2)}>#</MyAppText>
                                <MyAppText style={styles.agentsTableTableHeaderText(1)}>{translate('agentName')}</MyAppText>
                                <MyAppText style={styles.agentsTableTableHeaderText(1)}>{translate('workedHours')}</MyAppText>
                                <MyAppText style={styles.agentsTableTableHeaderText(1)}>{translate('workedWorkplaces')}</MyAppText>
                                <MyAppText style={styles.agentsTableTableHeaderText(1)}>{translate('withdrawals')}</MyAppText>
                            </View>
                            <View style={styles.agentsTableRowsContainer}>
                                {isLoading
                                    ? <View style={styles.loadingContainer}>
                                        <ActivityIndicator size='large' color={theme.colors.primary['500']} />
                                    </View>
                                    : <>
                                        {ensureMinimumLength(data?.agentsRanks ?? []).map((agent, index) =>
                                            <View key={index} style={styles.agentsTableRow}>
                                                <View style={styles.agentRankPositionContainer(windowInfo.width)}>
                                                    <FontAwesomeIcon icon={faStar} fontSize={45} color={getAgentPositionColor(index + 1)} />
                                                    <MyAppText style={styles.agentRankPosition}>
                                                        {index + 1}º
                                                    </MyAppText>

                                                </View>
                                                <MyAppText style={styles.agentName}>{agent.name}</MyAppText>
                                                <MyAppText style={styles.rowText}>{agent.totalWorkHours}h</MyAppText>
                                                <MyAppText style={styles.rowText}>{agent.workplaceCount}</MyAppText>
                                                <MyAppText style={styles.rowText}>{agent.dropoutCount}</MyAppText>
                                            </View>
                                        )}
                                    </>}
                            </View>
                        </View>
                    </View>
                    <RecentVacanciesDashboard
                        recentVacancies={data?.recentVacancies ?? []}
                        navigation={navigation}
                        isLoading={isLoading}
                    />
                </View>
            </ScrollView>
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    cardListWrapper: {
        flexDirection: 'column',
        gap: 16,
    },
    agentsTable: {
        padding: 16,
        gap: 16,
        flexDirection: 'column',
        backgroundColor: theme.colors.white['500'],
        borderWidth: 1,
        borderRadius: 4,
        borderColor: theme.colors.neutral['100']
    },
    agentsTableTableHeader: {
        paddingHorizontal: 16,
        flexDirection: 'row',
        paddingVertical: 8,
        backgroundColor: theme.colors.backgrounds['BG-2']
    },
    agentsTableTableHeaderText: (flex: number) => ({
        textAlign: 'center',
        flex: flex,
        fontSize: 12,
        color: theme.colors.neutral['500'],
        fontWeight: '600',
    }),
    agentsTableRowsContainer: {
        flexDirection: 'column',
        rowGap: 8,
    },
    agentsTableRow: {
        paddingVertical: 4,
        paddingHorizontal: 16,
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: 4,
        borderWidth: 1,
        borderColor: theme.colors.neutral['100']
    },
    contentContainer: {
        flexDirection: 'row',
        flex: 5,
        gap: 16
    },
    chartsAndAgentsTableContainer: (width: number) => ({
        gap: 16,
        flex: width <= 1024 ? 2 : 4
    }),
    multipleChartsContainer: (width: number) => ({
        gap: 16,
        flexDirection: width <= 1024 ? 'column' : 'row'
    }),
    chartContainer: (flex: number) => ({
        minHeight: 360,
        flex: flex
    }),
    chartHeaderContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
    },
    chartHeaderActions: {
        flexDirection: 'row',
        columnGap: 8,
        alignItems: 'center'
    },
    chartHeaderTitle: {
        fontSize: 16,
        color: theme.colors.secondary['400'],
        fontWeight: '600',
        flex: 0.75
    },
    chartHeaderPeriodText: {
        fontSize: 12,
        color: theme.colors.neutral['400'],
        fontWeight: '600',
    },
    agentName: {
        flex: 1,
        textAlign: 'center',
        fontSize: 14,
        color: theme.colors.neutral['400'],
        fontWeight: '700',
    },
    rowText: {
        flex: 1,
        textAlign: 'center',
        fontSize: 14,
        color: theme.colors.neutral['400'],
        fontWeight: '500',
    },
    actionsGroup: {
        flexDirection: 'row',
        gap: 8,
        alignItems: 'center'
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        height: 250
    },
    agentRankPositionContainer: (width: number) => ({
        flex: width <= 1024 ? 0.4 : 0.2,
        alignContent: 'center',
        position: 'relative',
        justifyContent: 'center'
    }),
    agentRankPosition: {
        position: 'absolute',
        left: '37%',
        textAlign: 'center',
        color: '#ffffff',
        fontWeight: '700',
        fontSize: 14
    }
}));
